// /vault, Overview (docs/08-app-design.md section 4; 5.1 steps 5 and 6 for
// a vault that does not exist on chain yet; website build brief section 9;
// round-4 brief section 6). Top to bottom: one account header (the vault's
// address, the SOL it holds and that figure's scope, Deposit / Swap / Send,
// and a single compact status line for the fee reserve and signing), then
// anything that needs attention as a notice with its next action (a pending
// key refresh, an unknown outcome, a fee reserve that cannot pay or could
// not be read), then the holdings (about two thirds) and the latest
// activity (one third). Loading is never shown as zero, and a failed read
// keeps the last figures with a notice that says so. Reading never signs
// and never costs.
// Before activation the setup rail (app/art/welcome/PLAN.md section 4.4)
// sits above its last two stops, the fee reserve and Activate; when
// Initialize lands, or when Restore hands over after its key refresh, the
// rail shows every stop done with "Setup complete" and a "Landed." line,
// and the ordinary overview follows directly beneath. After an unlock that
// rewrote an out-of-date stored fee reserve address, the page names the old
// address and its balance once per session (docs/18-one-fee-reserve.md
// section 3). On a legacy device whose older fee reserve holds SOL, the
// older reserve notice offers the move into the fee reserve (section 6,
// OlderReserve.tsx); a fee reserve that cannot pay points to it while the
// table can move it, and a refusal's own reading is shown in the notice, so
// the pointer leads to a visible Move. A move whose confirmation finished
// after a lock is shown once, from the session's 'sweep-outcome' notice.

import { type LeafRecord, NOT_APPLIED_NOTE_PREFIX, type VaultSigner } from '@thevault/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import { GasTankPanel, useGasTank } from '../components/GasTank';
import { KeyRefresh } from '../components/KeyRefresh';
import { LstValueLine } from '../components/LstValue';
import { OlderReserveNotice, OlderReservePointer, SweepOutcomeNotice, movableReading, useOlderReserve } from '../components/OlderReserve';
import { Address, Amount, Button, Notice, Qr, StatusLine, Toast, formatUnits, useToast } from '../components/primitives';
import { STEP_WORDS, stepState } from '../components/Review';
import { Shell } from '../components/Shell';
import { StakedGroup } from '../components/Staked';
import { type Flow, STEPS, StepRail } from '../components/StepRail';
import { WRONG_NETWORK_SENTENCE } from '../deployment';
import { ActionFailed, ActionUnknown, type ActivationResult, type ChainState, type Engine, InsufficientGas, type KeyStatus, type OlderReserve, type RefreshProgress, RefreshRequired, type WrapProgress, describe, pendingRefreshText } from '../engine';
import { type TokenAccountInfo } from '../rpc';
import { session, useSession } from '../session';
import { SAFE_STILL } from '../art/zq/safeStill';
import { FeeChip } from '../components/FeeCapsule';
import { KNOWN, WSOL_MINT_B58 } from '../tokens';
import { explorerBase, explorerUrl, shortSig, whenText } from '../util';

// Every fee reserve figure on this page is the engine's own requirement, read live (docs/18-one-fee-reserve.md 5.1 A;
// R78): activationCost() for Activate and its panel, the reading's plainAction (F + 15,000 + K) for the low-reserve
// notice, its refresh figure (F + 15,000) for the pending refresh, the wrap's own estimate for Make swappable, and
// "about N actions" from the same read. No page constant stands in for any of them.

/** The wrap reports the same steps and outcomes as every signed action (engine.ts, R45). */
const WRAP_WORDS = STEP_WORDS;

const TOKEN_PROGRAM = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
const TOKEN_2022 = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb';

/**
 * Activate's outcome as this page shows it (docs/18 5.1 D): the engine's result, or the page's own states around it
 * (`sending`, `checking`, `refused` before anything was sent).
 */
type Activation = { step: ActivationResult['step'] | 'sending' | 'checking' | 'refused'; signature: string | null; detail: string | null };

/**
 * DEV-only read-out for the activation tests (app/e2e/activation.spec.ts, R89): every Activate or Check again call
 * this page made, as it settled (the result's step, or "threw: <message>"), with the attempt the engine held right
 * after it. A call cancelled by a Lock or by another tab's Forget settles after this page has gone, so nothing on
 * screen says how it ended; this does. `import.meta.env.DEV` is a build-time constant (main.tsx's `__thevault_test`
 * relies on the same), so a production build records nothing.
 */
function noteActivationCall(engine: Engine, call: 'activate' | 'check', outcome: string): void {
  if (!import.meta.env.DEV) return;
  const w = window as unknown as { __thevault_test_activation?: Array<{ call: string; outcome: string; held: string | null }> };
  (w.__thevault_test_activation ??= []).push({ call, outcome, held: engine.heldActivation()?.signature ?? null });
}

// An unresolved key refresh (a leaf-255 recovery record) is worded by engine.ts `pendingRefreshText` (docs/18 5.1 C):
// this page passes its own landed-and-failed outcome for that very transaction while it holds it; a reload drops that
// outcome and the `sent` words return. Never "outcome unknown".

/**
 * Activate's outcome lines and its Check again (docs/18 5.1 D); `.activation-outcome` on the setup view and the
 * overview. Check again is offered whenever the engine still holds the attempt (`canCheck`, 5.1 D step 6), whatever the
 * last outcome said, except while a send or a check is under way.
 */
function ActivationOutcome({ activation, txBase, canCheck, onCheck }: { activation: Activation; txBase: string | null; canCheck: boolean; onCheck: () => void }) {
  const tx = activation.signature ? (
    <>
      {' '}
      as transaction{' '}
      {explorerUrl(txBase, activation.signature) ? (
        <a href={explorerUrl(txBase, activation.signature)!} target="_blank" rel="noreferrer">
          {shortSig(activation.signature)} ↗
        </a>
      ) : (
        shortSig(activation.signature)
      )}
    </>
  ) : null;
  const checkable = activation.step === 'checking' || (canCheck && activation.step !== 'sending');
  const line = (() => {
    switch (activation.step) {
      case 'sending':
        return (
          <StatusLine state="pending" word="Sending.">
            Initialize goes out from the fee reserve; this device waits until it is final.
          </StatusLine>
        );
      case 'checking':
        return (
          <StatusLine state="pending" word="Checking.">
            Asking the network about the activation{tx}.
          </StatusLine>
        );
      case 'landed':
        return (
          <StatusLine state="landed" word="Activated.">
            The vault exists on chain now{tx ? <>; it was created{tx}</> : null}.
          </StatusLine>
        );
      case 'exists':
        // An active vault and a closed one (a tombstone) both answer `exists` (R90): the words are true of either.
        return (
          <StatusLine state="neutral" word="Vault already exists.">
            It is on chain already, open or closed, so nothing was sent and no key marker was created or changed.
          </StatusLine>
        );
      case 'refused':
        return (
          <StatusLine state="failed" word="Not activated.">
            {activation.detail}
          </StatusLine>
        );
      case 'failed':
        return (
          <StatusLine state="failed" word="Not activated.">
            The activation was sent{tx}, and it landed and failed on chain, so this device writes no key marker. {activation.detail}
          </StatusLine>
        );
      case 'unknown':
        return (
          <StatusLine state="neutral" word="Outcome unknown.">
            The activation was sent{tx}, but this device has no proof yet that it is final, so it writes no key marker. {activation.detail ? `${activation.detail} ` : ''}Check again asks the network.
          </StatusLine>
        );
      case 'not-seen':
        return (
          <StatusLine state="neutral" word="Not seen yet.">
            The network has no record of the activation{tx}. Nothing was written: without that proof this device refreshes the vault&rsquo;s key before its first action.
          </StatusLine>
        );
      case 'not-final':
        return (
          <StatusLine state="pending" word="Not final yet.">
            The activation{tx} is confirmed but not finalized. Nothing is written until it is; check again in a few seconds.
          </StatusLine>
        );
      case 'not-visible':
        return (
          <StatusLine state="pending" word="Not visible yet.">
            The activation{tx} is final, but the network&rsquo;s finalized view does not show the vault account yet, so nothing is written yet. Check again in a few seconds.
          </StatusLine>
        );
      case 'differs':
        return (
          <StatusLine state="failed" word="Not activated.">
            The activation{tx} is final, but the vault on chain is not the untouched vault this device created, so no key marker is written. This device refreshes the vault&rsquo;s key before its first action.
          </StatusLine>
        );
      case 'none':
        return (
          <StatusLine state="neutral" word="Nothing to check.">
            This device no longer holds the activation it sent: a lock or a reload drops it. It refreshes the vault&rsquo;s key before its first action.
          </StatusLine>
        );
    }
  })();
  return (
    <div className="activation-outcome">
      {line}
      {checkable ? (
        <div className="actions">
          <Button small variant="ghost" onClick={onCheck} disabled={activation.step === 'checking'} working={activation.step === 'checking'}>
            Check again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * How this vault was set up, as Create or Restore handed it over (history state, this tab only): which flow's rail
 * to show, and whether its last stop (Restore's key refresh) just landed. Anything else is read as Create.
 */
function readHandover(): { flow: Flow; done: boolean } {
  const st = window.history.state as { setupFlow?: unknown; setupDone?: unknown } | null;
  const flow: Flow = st?.setupFlow === 'restore' || st?.setupFlow === 'restore-new' ? st.setupFlow : 'create';
  return { flow, done: st?.setupDone === true };
}

/** The setup rail on /vault: the stop under way ("Step 4 of 5 · Fee reserve"), or every stop done ("Setup complete"). */
function SetupProgress({ flow, current }: { flow: Flow; current: number }) {
  const steps = STEPS[flow];
  return (
    <section className="setup-progress" aria-label="Setup">
      <p className="stepline">
        {current > steps.length ? (
          'Setup complete'
        ) : (
          <>
            Step {current} of {steps.length}
            <span className="stop"> · {steps[current - 1]}</span>
          </>
        )}
      </p>
      <StepRail flow={flow} current={current} />
    </section>
  );
}

interface Holding extends TokenAccountInfo {
  decimals: number;
}

interface HomeData {
  state: ChainState;
  spendable: bigint;
  rentFloor: bigint;
  holdings: Holding[];
  /** Set when the RPC refused to list token accounts; SOL and the keys still show. */
  holdingsError: string | null;
  signer: VaultSigner | null;
  keys: KeyStatus | null;
  recent: LeafRecord[];
  signerError: string | null;
  /** Before activation: what Activate needs the fee reserve to hold, the engine's activationCost() (5.1 A). */
  activationNeed: bigint | null;
  /** With spendable SOL: what the wrap needs the fee reserve to hold, the wrap's own estimate (5.1 A). */
  wrapNeed: bigint | null;
}

async function load(engine: Engine): Promise<HomeData> {
  const state = await engine.readChain();
  const { spendable, rentFloor } = await engine.vaultLamports();
  const activationNeed = state.kind === 'absent' || state.kind === 'prefunded' ? (await engine.activationCost(state)).total : null;
  // Some public RPCs refuse the token-account index without a key; the vault, its SOL and its keys still show, and the notice says what is missing.
  let rows: TokenAccountInfo[] = [];
  let holdingsError: string | null = null;
  try {
    rows = (await Promise.all([engine.rpc.getTokenAccountsByOwner(engine.info.address, TOKEN_PROGRAM), engine.rpc.getTokenAccountsByOwner(engine.info.address, TOKEN_2022)])).flat();
  } catch (e) {
    holdingsError = describe(e);
  }
  const holdings: Holding[] = [];
  for (const r of rows) {
    if (r.amount === 0n) continue;
    let decimals = 0;
    try {
      decimals = await engine.rpc.getMintDecimals(r.mint);
    } catch {
      /* shown in raw units */
    }
    holdings.push({ ...r, decimals });
  }
  let signer: VaultSigner | null = null;
  let keys: KeyStatus | null = null;
  let recent: LeafRecord[] = [];
  let signerError: string | null = null;
  if (state.kind === 'vault' || state.kind === 'tombstone') {
    try {
      signer = await engine.signerFor(state);
      await engine.sync();
      keys = await engine.keyStatus(signer);
      // Records left unknown by an earlier attempt are checked before anything else (R45).
      if (keys.unresolved.length) {
        await engine.reconcile(signer);
        keys = await engine.keyStatus(signer);
      }
      recent = (await engine.history(signer)).slice(0, 3);
    } catch (e) {
      signerError = describe(e);
    }
  }
  let wrapNeed: bigint | null = null;
  if ((state.kind === 'vault' || state.kind === 'tombstone') && spendable > 0n) {
    try {
      wrapNeed = (await engine.wrapCost()).total;
    } catch {
      /* the button stays disabled without the wrap's own figure; the engine checks again at Confirm */
    }
  }
  return { state, spendable, rentFloor, holdings, holdingsError, signer, keys, recent, signerError, activationNeed, wrapNeed };
}

function purposeText(p: LeafRecord['purpose']): string {
  return { 'swap-exact': 'Swap', 'swap-bounded': 'Swap, bounded', transfer: 'Transfer', rotation: 'Key refresh', recovery: 'Key refresh', close: 'Close', other: 'Action' }[p];
}

/**
 * A record's status in the user's words (R45). "Burned" is the signer's
 * conservative decision to retire a key, not a chain fact by itself: the
 * note says what was seen (refused before sending, failed on chain,
 * expired unconfirmed), and a "sent" record whose outcome the app never
 * learned is exactly that, unknown. An unresolved key refresh (a recovery
 * record) is never called an unknown outcome: it is pending (docs/18 5.1 C).
 */
export function statusTag(s: LeafRecord['status'], note?: string, purpose?: LeafRecord['purpose']): { cls: string; word: string } {
  if (s === 'landed') return { cls: 'accent', word: 'LANDED' };
  if (purpose === 'recovery' && (s === 'sent' || s === 'signed' || s === 'reserved')) return { cls: 'pending', word: 'REFRESH PENDING' };
  if (s === 'sent') return { cls: 'pending', word: 'OUTCOME UNKNOWN' };
  if (s === 'signed' || s === 'reserved') return { cls: 'pending', word: 'IN PROGRESS' };
  const n = note ?? '';
  if (n.startsWith('rejected before sending')) return { cls: 'fail', word: 'NOT SENT' };
  if (n.startsWith('failed on chain')) return { cls: 'fail', word: 'FAILED ON CHAIN' };
  if (n.startsWith('expired')) return { cls: 'fail', word: 'EXPIRED' };
  // R69 row 6: the reconciliation step burned it because its tree moved on through another authorization, proven from that one's own transaction
  if (n.startsWith(NOT_APPLIED_NOTE_PREFIX)) return { cls: 'fail', word: 'NOT APPLIED' };
  return { cls: 'fail', word: 'KEY RETIRED' };
}

/**
 * The vault's still beside the account summary (APP-R3 §6): the regular safe of the landing page, sealed, its cobalt
 * door ring, on its detached shadow; the same in every account state; decoration only, never behind a figure.
 */
function SafeStill() {
  return <div className="account-art" aria-hidden="true" dangerouslySetInnerHTML={{ __html: SAFE_STILL }} />;
}

/** While the first read runs: the vault's identity, which is known locally, and quiet placeholders where figures will be. Never a zero. */
function Reading({ address }: { address: string }) {
  return (
    <>
      <section className="account reading" aria-labelledby="account-title" aria-busy="true">
        <div className="account-main">
          <div className="account-head">
            <h1 className="eyebrow" id="account-title">
              Your vault
            </h1>
            <Address value={address} kind="vault" />
          </div>
          <div className="account-figure">
            <span>SOL in your vault</span>
            <span className="skeleton skeleton-figure" aria-hidden="true" />
            <p className="reading-note" role="status">
              Reading the vault…
            </p>
          </div>
        </div>
        <SafeStill />
      </section>
      <div className="ov-grid" aria-hidden="true">
        <div className="panel skeleton-panel">
          <span className="skeleton skeleton-line" />
          <span className="skeleton skeleton-row" />
          <span className="skeleton skeleton-row" />
        </div>
        <div className="panel skeleton-panel">
          <span className="skeleton skeleton-line" />
          <span className="skeleton skeleton-row" />
        </div>
      </div>
    </>
  );
}

/**
 * The first read failed and nothing was read before: the vault's identity, which is known locally, the reason, and
 * Read again, in the place the figures would be. No figure is drawn at all, so nothing reads as zero.
 */
function NotRead({ address, error, reading, onRead }: { address: string; error: string; reading: boolean; onRead: () => void }) {
  return (
    <section className="account not-read" aria-labelledby="account-title">
      <div className="account-main">
        <div className="account-head">
          <h1 className="eyebrow" id="account-title">
            Your vault
          </h1>
          <Address value={address} kind="vault" />
        </div>
        <div className="account-figure">
          <span>SOL in your vault</span>
          <strong className="not-read-figure">Not read</strong>
          <StatusLine state="failed" word="Could not read the chain.">
            {/[.!?]$/.test(error.trim()) ? error : `${error}.`} Nothing is shown until a read succeeds, so no balance reads as zero.
          </StatusLine>
        </div>
        <div className="account-actions">
          <Button onClick={onRead} disabled={reading} working={reading}>
            {reading ? 'Reading' : 'Read again'}
          </Button>
        </div>
      </div>
      <SafeStill />
    </section>
  );
}

/**
 * The one-session notice of docs/18-one-fee-reserve.md section 3 (unlock step 5, R92): the fee reserve record this
 * device had stored is of a kind the writer cannot compare (storage.ts `compareStoredValue`), so unlock left it as it
 * is and wrote nothing over it; the derived reserve is in use, as always. The way to a readable record is Forget and
 * Restore, after any older reserve has been moved out (a legacy device's envelope goes with Forget, section 3).
 */
function FeeWalletUnreadable({ engine }: { engine: Engine }) {
  return (
    <Notice label="Fee reserve address" tone="warn">
      <p className="ink">
        The fee reserve address stored on this device could not be read, so it was left as it is; nothing is written over it. The app uses the fee reserve derived from your 24 words, <Address value={engine.relayerAddress} kind="fee reserve" full />. To replace the stored record, forget this device and restore it from your 24 words, after moving any older fee reserve out first.
      </p>
    </Notice>
  );
}

/**
 * The one-session notice of docs/18-one-fee-reserve.md section 3 (unlock step 5): the fee reserve address this device
 * had stored differed from the one derived from the words, and unlock rewrote it. Home names the old address and its
 * balance, read through the engine (its RPC, after its network check, 2.5); the notice lives in the session, not in
 * storage, so Dismiss, a lock or the next unlock ends it. The old address is only named: it is no funding destination.
 * The current address is a funding destination, so it appears only once the check has passed (2.5); before that, or
 * after a refusal, the notice points to the fee reserve panel, which runs the same check.
 */
function FeeWalletRewritten({ engine, previous }: { engine: Engine; previous: string }) {
  // null while the check runs; 'wrong-network' after a refusal; otherwise the check passed and the read answered
  const [balance, setBalance] = useState<{ lamports: bigint } | 'wrong-network' | 'unreadable' | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let reading: { lamports: bigint } | 'wrong-network' | 'unreadable';
      try {
        // throws only WrongNetwork, a failed genesis read included (engine.ts assertDeployment)
        await engine.assertDeployment();
        try {
          reading = { lamports: await engine.rpc.getBalance(previous) };
        } catch {
          reading = 'unreadable';
        }
      } catch {
        reading = 'wrong-network';
      }
      if (!cancelled) setBalance(reading);
    })();
    return () => {
      cancelled = true;
    };
  }, [engine, previous]);
  const checked = balance !== null && balance !== 'wrong-network';
  return (
    <Notice label="Fee reserve address" tone="warn">
      <p className="ink">
        The fee reserve address stored on this device was out of date. It was <Address value={previous} full />
        {balance === null ? (
          ' (reading its balance…)'
        ) : balance === 'wrong-network' ? (
          ', whose balance could not be read on this network'
        ) : balance === 'unreadable' ? (
          ', whose balance could not be read'
        ) : (
          <>
            , holding <Amount raw={balance.lamports} decimals={9} symbol="SOL" />
          </>
        )}
        {checked ? (
          <>
            ; the app now uses <Address value={engine.relayerAddress} kind="fee reserve" full />.
          </>
        ) : (
          '; the app now uses the fee reserve derived from your words; see the fee reserve panel.'
        )}
      </p>
      <div className="actions">
        <Button small variant="ghost" onClick={() => session.dismissNotice()}>
          Dismiss
        </Button>
      </div>
    </Notice>
  );
}

export function Vault() {
  const { settings, notice } = useSession();
  const engine = session.engine();
  const [data, setData] = useState<HomeData | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** When the figures on screen were read; shown only when a later read fails. */
  const [readAt, setReadAt] = useState<number | null>(null);
  const [rereading, setRereading] = useState(false);
  const [activation, setActivation] = useState<Activation | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  /** The balance the setup view's fee reserve panel last read: Activate's own gate. */
  const [tankLamports, setTankLamports] = useState<bigint | null>(null);
  /** This session's landed-and-failed key refresh, by its transaction, while this page is mounted (docs/18 5.1 C). */
  const [refreshFailedOnChain, setRefreshFailedOnChain] = useState<string | null>(null);
  const { tank, error: tankError, network: tankNetwork, refresh: refreshTank } = useGasTank(engine);
  const [toast, say] = useToast();
  const [wrapProgress, setWrapProgress] = useState<WrapProgress | null>(null);
  const [handover] = useState(readHandover);
  // This device's older fee reserve (docs/18 section 6), read at every visit after this page's own read, whose ledger
  // sync settles the records the sweep's guard reads (R94): reloading the overview, or opening it again, is the path
  // that clears a blocked move. Null on a device that stores none.
  const older = useOlderReserve(engine, { readAtMount: false });
  // A reserve that cannot pay points to the move while the table can move the older one (section 6): from this page's
  // own reading for its notices, or from the refusal itself (InsufficientGas.movable) for Activate and the wrap.
  const movable = movableReading(older);
  const [activationMovable, setActivationMovable] = useState<OlderReserve | null>(null);
  const [wrapMovable, setWrapMovable] = useState<OlderReserve | null>(null);
  // Restore's "just landed" is said once: a reload of this entry shows the plain overview.
  useEffect(() => {
    if (!handover.done) return;
    const st = window.history.state as Record<string, unknown> | null;
    window.history.replaceState({ ...st, setupDone: false }, '');
  }, [handover.done]);
  // Activate sits low on the page and the setup steps vanish when it lands: bring "Setup complete" into view once,
  // and take focus there, since the focused Activate button unmounts with the steps and focus would fall to the body.
  const doneRef = useRef<HTMLDivElement>(null);
  const activated = activation?.step === 'landed';
  const showDone = (activated || handover.done) && (data?.state.kind === 'vault' || data?.state.kind === 'tombstone');
  useEffect(() => {
    if (!showDone) return;
    doneRef.current?.scrollIntoView({ block: 'start' });
    doneRef.current?.focus({ preventScroll: true });
  }, [showDone]);
  /** The setup flow this vault came through, for the rail before activation (only Create or a restore that found no vault reach it). */
  const setupFlow: Flow = handover.flow === 'restore-new' ? 'restore-new' : 'create';
  const wrapBusy = wrapProgress ? wrapProgress.step === 'signing' || wrapProgress.step === 'checking' || wrapProgress.step === 'sending' : false;

  const refreshOlder = older.refresh;
  const reload = useCallback(async () => {
    try {
      setData(await load(engine));
      setReadAt(Date.now());
      setError(null);
    } catch (e) {
      setError(describe(e));
    }
    // The older reserve's reading follows this page's read: its sync has settled the guard's records by now (R94).
    void refreshOlder();
  }, [engine, refreshOlder]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const readAgain = async () => {
    setRereading(true);
    try {
      await reload();
    } finally {
      setRereading(false);
    }
  };

  /**
   * Activate (docs/18 5.1 D): a refusal before the send says why; once sent, the engine's own outcome is shown, and
   * the page reads the vault again, since it may exist now (this device's carrier, or another device's).
   */
  const activate = async () => {
    setActivation({ step: 'sending', signature: null, detail: null });
    setActivationMovable(null);
    let result: ActivationResult;
    try {
      result = await engine.activate();
    } catch (e) {
      noteActivationCall(engine, 'activate', `threw: ${e instanceof Error ? e.message : String(e)}`);
      setActivation({ step: 'refused', signature: null, detail: describe(e) });
      if (e instanceof InsufficientGas) {
        setActivationMovable(e.movable);
        // the notice shows the refusal's reading, so the pointer's #older-reserve has a target with a Move (docs/18 section 6)
        if (e.movable) older.adopt(e.movable);
      }
      return;
    }
    noteActivationCall(engine, 'activate', result.step);
    setActivation(result);
    await reload();
    await refreshTank();
  };

  /** Check again (5.1 D step 6): the engine reads the held signature's status once; only finalized success writes the marker. */
  const checkActivation = async () => {
    const signature = activation?.signature ?? engine.heldActivation()?.signature ?? null;
    setActivation({ step: 'checking', signature, detail: null });
    let result: ActivationResult;
    try {
      result = await engine.checkActivation();
    } catch (e) {
      noteActivationCall(engine, 'check', `threw: ${e instanceof Error ? e.message : String(e)}`);
      setActivation({ step: 'unknown', signature, detail: `The check could not be completed: ${describe(e)}` });
      return;
    }
    noteActivationCall(engine, 'check', result.step);
    setActivation(result);
    if (result.step === 'landed' || result.step === 'failed') {
      await reload();
      await refreshTank();
    }
  };
  // A held attempt this page did not start (it was left and opened again in the same session) is shown as unknown.
  const held = engine.heldActivation();
  const shownActivation: Activation | null = activation ?? (held ? { step: 'unknown', signature: held.signature, detail: null } : null);

  const wrap = async () => {
    if (!data?.signer || data.spendable <= 0n) return;
    setWrapProgress({ step: 'signing' });
    setWrapMovable(null);
    try {
      await engine.wrapStray(data.signer, data.spendable, setWrapProgress);
    } catch (e) {
      if (e instanceof RefreshRequired) {
        // Nothing was signed: the refresh runs first, in the notice below, and the form comes back.
        setWrapProgress(null);
        setRefreshing(true);
      } else if (!(e instanceof ActionFailed) && !(e instanceof ActionUnknown)) {
        // Not one of the pipeline's own outcomes (the program check, a stale slot, the gas tank): only report it.
        setWrapProgress({ step: 'rejected', error: describe(e) });
        if (e instanceof InsufficientGas) {
          setWrapMovable(e.movable);
          // as for Activate: the notice shows the reading the pointer names
          if (e.movable) older.adopt(e.movable);
        }
      }
      // ActionFailed / ActionUnknown are already reported through wrapProgress by the pipeline itself.
    } finally {
      await reload();
      await refreshTank();
    }
  };

  const checkUnresolved = async () => {
    if (!data?.signer) return;
    await engine.reconcile(data.signer);
    await reload();
    // A wrap's own "Outcome unknown." line is superseded by this check: the notice above (still unresolved) or the
    // activity list (resolved, with its real outcome) now says what happened, so the stale line does not linger.
    setWrapProgress((p) => (p?.step === 'unknown' ? null : p));
  };

  const addrBase = explorerBase(settings.rpcUrl, 'address');
  const txBase = explorerBase(settings.rpcUrl, 'tx');
  const detail = data ? (data.state.kind === 'vault' ? `KEY TREE ${data.state.snap.treeIndex}` : data.state.kind === 'absent' || data.state.kind === 'prefunded' ? 'NOT YET ACTIVATED' : data.state.kind.toUpperCase()) : error ? 'NOT READ' : 'READING';
  const explorer = addrBase ? explorerUrl(addrBase, '')! : undefined;

  return (
    <Shell number="01" title="Overview" detail={detail}>
      {error && !data ? <NotRead address={engine.info.address} error={error} reading={rereading} onRead={() => void readAgain()} /> : null}
      {error && data ? (
        <section className="ov-alert" aria-label="Read failed">
          <StatusLine state="failed" word="Could not read the chain.">
            {/[.!?]$/.test(error.trim()) ? error : `${error}.`}{' '}
            {readAt ? `The figures below are from the last successful read, at ${whenText(readAt)}, and may be out of date.` : 'The figures below are from an earlier read and may be out of date.'}
          </StatusLine>
          <div className="actions">
            <Button small variant="ghost" onClick={() => void readAgain()} disabled={rereading} working={rereading}>
              {rereading ? 'Reading' : 'Read again'}
            </Button>
          </div>
        </section>
      ) : null}
      {notice?.kind === 'fee-wallet-rewritten' ? <FeeWalletRewritten engine={engine} previous={notice.previous} /> : null}
      {notice?.kind === 'fee-wallet-unreadable' ? <FeeWalletUnreadable engine={engine} /> : null}
      {notice?.kind === 'sweep-outcome' ? <SweepOutcomeNotice outcome={notice} /> : null}
      <OlderReserveNotice
        engine={engine}
        older={older}
        onMoved={() => {
          // the refusals' readings are from before the move
          setActivationMovable(null);
          setWrapMovable(null);
          void refreshTank();
          void reload();
        }}
      />
      {!data && !error ? <Reading address={engine.info.address} /> : null}

      {data?.state.kind === 'absent' || data?.state.kind === 'prefunded' ? (
        <>
          <section className="account setup" aria-labelledby="overview-title">
            <div className="account-main">
              <div className="account-head">
                <span className="eyebrow">Not yet activated</span>
                <Address value={engine.info.address} kind="vault" explorer={explorer} onCopied={() => say('Copied')} />
              </div>
              <div className="account-figure">
                <h1 id="overview-title">Your vault.</h1>
                <p className="ink">Two steps make it yours: add a little SOL to its fee reserve, then activate it. Deposits can arrive at its address before that.</p>
              </div>
            </div>
            <SafeStill />
          </section>
          <SetupProgress flow={setupFlow} current={tankLamports !== null && data.activationNeed !== null && tankLamports >= data.activationNeed ? 5 : 4} />
          <div className="ov-grid setup-steps">
            <div className="stack">
              <span className="eyebrow">
                <span className="step-index">4</span> Fund the fee reserve
              </span>
              <GasTankPanel engine={engine} needed={data.activationNeed} onBalance={setTankLamports} />
            </div>
            <div className="stack">
              <span className="eyebrow">
                <span className="step-index">5</span> Activate
              </span>
              <div className="panel">
                <p>
                  Activation creates the vault account on chain in one transaction, paid from the fee reserve.{' '}
                  {data.activationNeed !== null ? (
                    <>
                      The fee reserve needs at least <Amount raw={data.activationNeed} decimals={9} symbol="SOL" maxFraction={9} /> first: its own account minimum, the fee, one key refresh held back
                      {data.state.kind === 'prefunded' && data.activationNeed !== null ? ', and whatever rent the deposit already there does not cover.' : ', and the vault account’s rent.'}
                    </>
                  ) : null}
                </p>
                {shownActivation ? <ActivationOutcome activation={shownActivation} txBase={txBase} canCheck={held !== null} onCheck={() => void checkActivation()} /> : null}
                {(() => {
                  const pointTo = activationMovable ?? (tankLamports !== null && data.activationNeed !== null && tankLamports < data.activationNeed ? movable : null);
                  return pointTo ? <OlderReservePointer reading={pointTo} here /> : null;
                })()}
                <div className="actions">
                  <Button
                    onClick={() => void activate()}
                    disabled={activation?.step === 'sending' || activation?.step === 'checking' || tankLamports === null || data.activationNeed === null || tankLamports < data.activationNeed}
                    working={activation?.step === 'sending'}
                  >
                    {activation?.step === 'sending' ? 'Sending' : 'Activate'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <section className="stack" aria-labelledby="vault-address-title">
            <span className="eyebrow" id="vault-address-title">
              Your vault&rsquo;s address
            </span>
            <Notice label="Address">
              <div className="address-block">
                <Qr text={engine.info.address} label="QR code of the vault address" />
                <div className="stack tight">
                  <Address value={engine.info.address} kind="vault" full explorer={explorer} onCopied={() => say('Copied')} />
                  <p className="ink">Deposits are safe now, even before the vault is activated. SOL sent here stays in the vault as native SOL; you make it swappable here with Make swappable.</p>
                  {data.state.kind === 'prefunded' ? (
                    <p>
                      Already holds <Amount raw={data.state.account.lamports} decimals={9} symbol="SOL" />.
                    </p>
                  ) : data.spendable > 0n ? (
                    <p>
                      Already holds <Amount raw={data.spendable + data.rentFloor} decimals={9} symbol="SOL" />.
                    </p>
                  ) : null}
                </div>
              </div>
            </Notice>
          </section>
        </>
      ) : null}

      {data && (data.state.kind === 'vault' || data.state.kind === 'tombstone')
        ? (() => {
            const wsol = data.holdings.find((h) => h.mint === WSOL_MINT_B58);
            const others = data.holdings.filter((h) => h.mint !== WSOL_MINT_B58);
            const solTotal = data.spendable + (wsol?.amount ?? 0n);
            // An RPC that refused the token listing says nothing about wrapped SOL or tokens: that is not an empty vault.
            const empty = data.spendable === 0n && data.holdings.length === 0 && !data.holdingsError;
            // One plain action, F + 15,000 + K, from the engine's own reading (5.1 A).
            const tankLow = !!tank && tank.lamports < tank.plainAction;
            // At four decimals a reserve below 0.0001 SOL would read as "0"; a small one is shown to the lamport instead.
            const tankFraction = tank && tank.lamports < 1_000_000n ? 9 : 4;
            // What a row may promise: "ready" only while the fee reserve was read and can pay, the key is usable and nothing is unresolved.
            const canAct = !!tank && !tankLow && !tankError && !!data.keys && !data.signerError && !data.keys.unresolved.length;
            const assetCount = 1 + data.holdings.length;
            // An unresolved key refresh (its leaf-255 record) has its own notice and is never an unknown outcome (5.1 C).
            const recovery = data.keys?.unresolved.find((r) => r.purpose === 'recovery') ?? null;
            const ordinaryUnresolved = data.keys?.unresolved.filter((r) => r.purpose !== 'recovery') ?? [];
            const refreshPending = !!data.keys && (!!data.keys.refreshReason || !!recovery);
            const refreshSentence = recovery ? pendingRefreshText(recovery, refreshFailedOnChain) : `${data.keys?.refreshReason ?? ''} The vault will refresh its key before your next action, or now.`;
            // The refresh's own requirement, F + 15,000, against the page's latest reading (its panel's Check again reads it again).
            const liveTank = tank?.lamports ?? null;
            const refreshNeed = tank?.refresh ?? null;
            const refreshShort = liveTank !== null && refreshNeed !== null && liveTank < refreshNeed ? refreshNeed - liveTank : null;
            const wrapShort = data.spendable > 0n && !!tank && data.wrapNeed !== null && tank.lamports < data.wrapNeed;
            return (
              <>
                {showDone ? (
                  <div className="setup-done" ref={doneRef} tabIndex={-1}>
                    <SetupProgress flow={activated ? setupFlow : 'restore'} current={(activated ? STEPS[setupFlow] : STEPS.restore).length + 1} />
                    {/* Only the confirmed outcome (R66): landing is not readiness. Whether this device can sign is the
                        overview's own fact below ("Signing ready", or "Key not usable." when the signer read failed). */}
                    <StatusLine state="landed" word="Landed.">
                      {activated ? 'Activation is on chain.' : 'The key refresh is on chain.'}
                    </StatusLine>
                  </div>
                ) : null}
                {shownActivation && shownActivation.step !== 'landed' && shownActivation.step !== 'sending' ? (
                  <Notice label="Activation" tone="warn">
                    <ActivationOutcome activation={shownActivation} txBase={txBase} canCheck={held !== null} onCheck={() => void checkActivation()} />
                  </Notice>
                ) : null}
                <section className={`account ${empty ? 'empty' : 'funded'}`} aria-labelledby="account-title">
                  <div className="account-main">
                    <div className="account-head">
                      <h1 className="eyebrow" id="account-title">
                        Your vault
                      </h1>
                      <Address value={engine.info.address} kind="vault" explorer={explorer} onCopied={() => say('Copied')} />
                    </div>
                    <div className="account-row">
                    {empty ? (
                      <div className="account-figure">
                        <strong>Make this vault yours.</strong>
                        <p className="ink">Deposit SOL or tokens to its address. Network fees come from a separate fee reserve, shown in the line below; keep a little SOL there too.</p>
                      </div>
                    ) : (
                      <div className="account-figure">
                        <span>SOL in your vault</span>
                        <strong className="num">
                          <Amount raw={solTotal} decimals={9} symbol="SOL" maxFraction={4} />
                        </strong>
                        <small>
                          {data.holdingsError
                            ? 'Native SOL only; wrapped SOL and tokens could not be listed (see Your holdings). The SOL kept for the account and the fee reserve are not counted. '
                            : 'Native and wrapped SOL together; the SOL kept for the account and the fee reserve are not counted. '}
                          {others.length ? <span className="ink">And {others.length} other {others.length === 1 ? 'token' : 'tokens'}, listed below.</span> : null}
                        </small>
                      </div>
                    )}
                    <FeeChip tank={tank} error={tankError} />
                    </div>
                    <div className="account-actions">
                      <Link href="/deposit" className="button">
                        {empty ? 'Deposit assets' : 'Deposit'}
                      </Link>
                      <Link href="/swap" className="button ghost">
                        Swap
                      </Link>
                      <Link href="/withdraw" className="button ghost">
                        Send
                      </Link>
                    </div>
                  </div>
                  <SafeStill />

                  <details className="status-strip">
                    <summary>
                      <span className="status-item" data-tone={tank ? (tankError ? 'warn' : tankLow ? 'bad' : 'ok') : tankError ? 'warn' : 'plain'}>
                        {tank
                          ? `Fee reserve ${formatUnits(tank.lamports, 9, tankFraction).text} SOL${tankError ? ', last read' : tankLow ? ', too low' : ''}`
                          : tankError
                            ? 'Fee reserve: could not check'
                            : 'Fee reserve: checking'}
                      </span>
                      <span className="status-item" data-tone={data.keys ? (refreshPending ? 'warn' : 'ok') : data.signerError ? 'bad' : 'plain'}>
                        {data.keys ? (refreshPending ? 'Key refresh pending' : 'Signing ready') : data.signerError ? 'Signing not available' : 'Signing: checking'}
                      </span>
                      <span className="status-strip-more">Details</span>
                    </summary>
                    <div className="console-metrics">
                      <div>
                        <span>Fee reserve</span>
                        {tank ? (
                          <>
                            <strong className="metric-state">
                              <Amount raw={tank.lamports} decimals={9} symbol="SOL" maxFraction={tankFraction} />
                              {tankError ? <span className="tag pending">LAST READ</span> : tankLow ? <span className="tag fail">TOO LOW</span> : null}
                            </strong>
                            <small>
                              {tankError ? 'The latest check failed; this is the last balance read. ' : null}
                              {`About ${tank.actions} ${tank.actions === 1 ? 'action' : 'actions'}, keeping its account minimum and one key refresh in reserve. `}
                              Pays network fees and account rent. It is outside your vault&rsquo;s holdings; every device that opens this vault uses it, and your 24 words restore it.
                            </small>
                          </>
                        ) : tankError ? (
                          <>
                            <strong>Could not check</strong>
                            <small>{tankNetwork === 'wrong' ? WRONG_NETWORK_SENTENCE : 'The balance could not be read, so it is not shown as zero.'}</small>
                          </>
                        ) : (
                          <>
                            <strong>Checking…</strong>
                            <small>Reading the fee reserve&rsquo;s balance.</small>
                          </>
                        )}
                      </div>
                      <div>
                        <span>Signing keys</span>
                        <strong>{data.keys ? (refreshPending ? 'Refresh pending' : 'Ready') : data.signerError ? 'Not usable' : 'Checking…'}</strong>
                        <small>
                          {data.keys
                            ? `${data.keys.usedLeaves} of 256 one-time keys used on tree ${data.keys.treeIndex}. The vault moves to fresh keys automatically in ${data.keys.actionsLeft} ${data.keys.actionsLeft === 1 ? 'action' : 'actions'}.`
                            : data.signerError ?? ''}
                        </small>
                      </div>
                    </div>
                  </details>
                </section>

                {data.signerError ? (
                  <StatusLine state="failed" word="Key not usable.">
                    {data.signerError}
                  </StatusLine>
                ) : null}

                {refreshPending && data.signer ? (
                  // The pending refresh (5.1 C): an unresolved recovery record in its own words, else the reason a refresh
                  // is due. Below the refresh's requirement (F + 15,000) the notice shows the amount to add with the fee
                  // reserve panel, and the button waits for the balance; at or above it, the refresh runs (and reuses an
                  // existing record: the same signature, no new key).
                  <Notice label="Key refresh pending" tone="warn">
                    {refreshing ? (
                      <KeyRefresh
                        engine={engine}
                        signer={data.signer}
                        reason={refreshSentence}
                        older={older}
                        onDone={() => {
                          setRefreshing(false);
                          void reload();
                        }}
                        onProgress={(p: RefreshProgress) => {
                          if (p.step === 'failed' && p.kind === 'failed' && p.signature) setRefreshFailedOnChain(p.signature);
                          // the record's state may have moved (signed, sent): read it again so the sentence follows it
                          if (p.step === 'failed' || p.step === 'unknown') void reload();
                        }}
                      />
                    ) : (
                      <>
                        <p className="ink">{refreshSentence}</p>
                        {refreshShort !== null && refreshNeed !== null && liveTank !== null ? (
                          <>
                            <p className="refresh-shortfall">
                              Add at least <Amount raw={refreshShort} decimals={9} symbol="SOL" maxFraction={9} /> to the fee reserve first: the refresh needs <Amount raw={refreshNeed} decimals={9} symbol="SOL" maxFraction={9} />, and it holds{' '}
                              <Amount raw={liveTank} decimals={9} symbol="SOL" maxFraction={9} />.
                            </p>
                            {movable ? <OlderReservePointer reading={movable} here /> : null}
                            <GasTankPanel engine={engine} needed={refreshNeed} onBalance={() => void refreshTank()} />
                          </>
                        ) : null}
                        <div className="actions">
                          <Button small onClick={() => setRefreshing(true)} disabled={refreshShort !== null}>
                            {recovery ? 'Retry the refresh' : 'Refresh now'}
                          </Button>
                        </div>
                      </>
                    )}
                  </Notice>
                ) : null}

                {ordinaryUnresolved.length ? (
                  // A record is marked sent before its wire leaves (engine.ts sendWire), so one still reserved or signed
                  // was never sent from this device (a lock stopped it before the send, say): say that, not "sent".
                  <Notice label={ordinaryUnresolved.some((r) => r.status === 'sent') ? 'Outcome unknown' : 'Not sent'} tone="warn">
                    <p className="ink">{ordinaryUnresolved.some((r) => r.status === 'sent') ? 'An earlier attempt was sent but not confirmed. Nothing new is signed until its outcome is known.' : 'An earlier attempt was prepared on this device but never sent. Nothing new is signed until it is resolved.'}</p>
                    <div className="actions">
                      <Button small onClick={() => void checkUnresolved()}>
                        Check again
                      </Button>
                    </div>
                  </Notice>
                ) : null}

                {tankLow && !tankError ? (
                  <Notice label="Fee reserve" tone="fail">
                    <p className="ink">The fee reserve cannot pay for the next action. Add SOL to your fee reserve to cover it. Your vault holdings are still there.</p>
                    {movable ? <OlderReservePointer reading={movable} here /> : null}
                    <GasTankPanel engine={engine} needed={tank ? tank.plainAction : null} onBalance={() => void refreshTank()} />
                  </Notice>
                ) : null}

                {tankError ? (
                  <Notice label="Fee reserve" tone="warn">
                    <p className="ink">
                      {tank ? (
                        <>
                          The fee reserve could not be read just now. The balance shown, <Amount raw={tank.lamports} decimals={9} symbol="SOL" maxFraction={tankFraction} />, is from the last successful read
                          {tankLow ? ', and was too low for the next action then; add SOL on the Deposit page' : ''}. Your vault holdings are unaffected.
                        </>
                      ) : (
                        <>The fee reserve&rsquo;s balance could not be read, so it is not shown as zero. Your vault holdings are unaffected.</>
                      )}
                    </p>
                    <div className="actions">
                      <Button small variant="ghost" onClick={() => void refreshTank()}>
                        Check again
                      </Button>
                    </div>
                  </Notice>
                ) : null}

                <div className="ov-grid">
                  <section className="panel holdings" aria-labelledby="holdings-title">
                    <div className="console-heading">
                      <h3 id="holdings-title">Your holdings</h3>
                      <span className="dim">{empty ? 'NONE YET' : `${String(assetCount).padStart(2, '0')} ASSETS`}</span>
                    </div>
                    {empty ? (
                      <div className="holdings-empty">
                        <p className="activity-empty">Nothing held yet. What you deposit appears here, one row per asset, with what each can do.</p>
                        <p className="activity-note">
                          The vault account itself keeps <Amount raw={data.rentFloor} decimals={9} symbol="SOL" maxFraction={9} /> so it stays open; that is not counted as a holding.
                        </p>
                      </div>
                    ) : (
                      <table className="table assets">
                        <thead className="sr-only">
                          <tr>
                            <th>Asset</th>
                            <th>In your vault</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wsol ? (
                            <tr key={wsol.pubkey}>
                              <td>
                                <span className="asset-id">
                                  <span className="token-glyph t-sol" aria-hidden="true">
                                    S
                                  </span>
                                  <span>
                                    SOL (wrapped)<small>{KNOWN[wsol.mint]?.name ?? 'Wrapped SOL, swappable'}</small>
                                  </span>
                                </span>
                              </td>
                              <td>
                                <span className="num">
                                  <Amount raw={wsol.amount} decimals={wsol.decimals} symbol="SOL" />
                                </span>
                                <small className="avail">{canAct ? 'Ready to swap or send' : 'Held in the vault'}</small>
                              </td>
                            </tr>
                          ) : null}
                          <tr className={wsol ? 'grouped' : undefined}>
                            <td>
                              <span className="asset-id">
                                <span className="token-glyph t-sol" aria-hidden="true">
                                  S
                                </span>
                                <span>
                                  SOL<small>Native. Swappable once you choose Make swappable.</small>
                                </span>
                              </span>
                            </td>
                            <td>
                              <span className="num">
                                <Amount raw={data.spendable} decimals={9} symbol="SOL" />
                              </span>
                              {data.spendable > 0n ? <small className="avail ink">{canAct ? 'Sendable; not yet swappable' : 'Held in the vault; not yet swappable'}</small> : null}
                              <details>
                                <summary>Kept for the account</summary>
                                <small>
                                  The vault account keeps <Amount raw={data.rentFloor} decimals={9} symbol="SOL" maxFraction={9} /> so it stays open. It is not part of the amount above.
                                </small>
                              </details>
                              {data.spendable > 0n ? (
                                <div className="actions">
                                  <Button small variant="ghost" onClick={() => void wrap()} disabled={wrapBusy || !data.signer || !tank || data.wrapNeed === null || wrapShort} working={wrapBusy}>
                                    {wrapBusy ? 'Wrapping' : 'Make swappable'}
                                  </Button>
                                </div>
                              ) : null}
                              {wrapShort ? <p className="ink">The fee reserve cannot pay for it yet.</p> : null}
                              {(() => {
                                const pointTo = wrapMovable ?? (wrapShort ? movable : null);
                                return pointTo ? <OlderReservePointer reading={pointTo} here /> : null;
                              })()}
                              {wrapProgress ? (
                                <StatusLine state={stepState(wrapProgress.step)} word={WRAP_WORDS[wrapProgress.step]}>
                                  {wrapProgress.step === 'landed' ? (
                                    <>
                                      The vault's wSOL account now holds it
                                      {wrapProgress.signature && explorerUrl(txBase, wrapProgress.signature) ? (
                                        <>
                                          , transaction{' '}
                                          <a href={explorerUrl(txBase, wrapProgress.signature)!} target="_blank" rel="noreferrer">
                                            {shortSig(wrapProgress.signature)} ↗
                                          </a>
                                        </>
                                      ) : null}
                                      .
                                    </>
                                  ) : wrapProgress.step === 'failed' || wrapProgress.step === 'rejected' || wrapProgress.step === 'unknown' ? (
                                    wrapProgress.error
                                  ) : (
                                    "One action moves the SOL into the vault's wSOL account, paid from the fee reserve."
                                  )}
                                </StatusLine>
                              ) : null}
                            </td>
                          </tr>
                          {others.map((h) => {
                            const known = KNOWN[h.mint];
                            const symbol = known?.symbol ?? `${h.mint.slice(0, 4)}…${h.mint.slice(-4)}`;
                            return (
                              <tr key={h.pubkey}>
                                <td>
                                  <span className="asset-id">
                                    <span className={`token-glyph ${known ? `t-${known.symbol.toLowerCase()}` : 't-unknown'}`} aria-hidden="true">
                                      {known ? symbol.slice(0, 1) : '?'}
                                    </span>
                                    <span>
                                      {symbol}
                                      <small>{known?.name ?? `mint ${h.mint}`}</small>
                                    </span>
                                  </span>
                                </td>
                                <td>
                                  <span className="num">
                                    <Amount raw={h.amount} decimals={h.decimals} symbol={known?.symbol ?? 'units'} />
                                  </span>
                                  <small className="avail">{!known ? 'Unlisted token, named by its mint' : canAct ? 'Ready to swap or send' : 'Held in the vault'}</small>
                                  <LstValueLine rpc={engine.rpc} mint={h.mint} amount={h.amount} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                    <StakedGroup engine={engine} rpcUrl={settings.rpcUrl} version={readAt} />
                    {data.holdingsError ? (
                      <StatusLine state="neutral" word="Tokens not listed.">
                        This RPC endpoint does not list token accounts: {data.holdingsError} SOL and the keys are unaffected; another endpoint in Settings lists tokens too.
                      </StatusLine>
                    ) : null}
                    {empty ? null : <p className="activity-note">Quantities only: prices are not shown yet, so there is no total in dollars.</p>}
                  </section>

                  <section className="panel activity-list" aria-labelledby="activity-title">
                    <div className="console-heading">
                      <h3 id="activity-title">Latest activity</h3>
                      <span className="dim">{data.recent.length ? `LAST ${data.recent.length}` : 'NOTHING YET'}</span>
                    </div>
                    {data.recent.length ? (
                      <table className="table">
                        <tbody>
                          {data.recent.map((r) => {
                            const t = statusTag(r.status, r.note, r.purpose);
                            return (
                              <tr key={r.key}>
                                <td>
                                  {purposeText(r.purpose)}
                                  <small className="mono">{whenText(r.updatedAt)}</small>
                                </td>
                                <td>
                                  <span className={`tag ${t.cls}`}>{t.word}</span>
                                  {r.txSignature ? <small className="mono">{explorerUrl(txBase, r.txSignature) ? <a href={explorerUrl(txBase, r.txSignature)!} target="_blank" rel="noreferrer">{shortSig(r.txSignature)} ↗</a> : shortSig(r.txSignature)}</small> : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <p className="activity-empty">Swaps, sends and key refreshes you sign appear here with their status and transaction.</p>
                    )}
                    <p className="activity-note">Signed on this device only; other devices keep their own list.</p>
                  </section>
                </div>
              </>
            );
          })()
        : null}

      {data?.state.kind === 'foreign' ? (
        <Notice label="Address in use" tone="fail">
          <p className="ink">The address this phrase names holds an account that is not a vault of this program. Nothing can be done with it from here.</p>
        </Notice>
      ) : null}
      <Toast message={toast} />
    </Shell>
  );
}
